import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { rem } from 'polished'
import styled from 'styled-components'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import {
  formatDate,
  getOccupancyDescription,
} from 'components/SearchV2/Utils/GlobalSearchV2'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import TravellersFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/TravellersFloatingPanel'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { EmptyArray } from 'lib/array/arrayUtils'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

export enum searchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
}

interface Props {
  onSearch: () => void
  showSearch?: (show: boolean) => void
  updateSearchSessionId?: (newSessionId: string) => void
}

function HomesAndVillasTabContent(props: Props) {
  const { onSearch, updateSearchSessionId } = props
  const intl = useIntl()
  const locationLabels = useLocationSearchLabels()

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies = EmptyArray,
    areRoomErrorsShown,
    flexibleMonths,
    flexibleNights,
    dateSearchOptionId,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const { openedSearchVertical, option } = useGlobalSearchURLHashVertical()

  const isDateSelectOpen = openedSearchVertical === 'homes-and-villas' && option === 'dateSelect'

  const stepsValidation = useMemo(
    () => ({
      [searchSteps.LOCATION]: searchItem,
      [searchSteps.DATE]:
        (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME || (checkinDate && checkoutDate) || flexibleMonths) ||
        (checkinDate && checkoutDate),
      [searchSteps.GUESTS]: !areRoomErrorsShown,
    }),
    [searchItem, dateSearchOptionId, checkinDate, checkoutDate, flexibleMonths, areRoomErrorsShown],
  )

  const flexibleSearchSelectionText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, undefined, intl)

  const [activeStep, setActiveStep] = useState<searchSteps>()
  const { goToNextStep, goToPreviousStep, closeActiveStep, isSearchEnabled } =
    useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const openLocationSearchStep = useCallback(
    () => setActiveStep(searchSteps.LOCATION),
    [setActiveStep],
  )
  const openDateSearchStep = useCallback(
    () => setActiveStep(searchSteps.DATE),
    [setActiveStep],
  )
  const openGuestsSearchStep = useCallback(
    () => setActiveStep(searchSteps.GUESTS),
    [setActiveStep],
  )

  useEffect(() => {
    if (isDateSelectOpen) {
      openDateSearchStep()
    }
  }, [isDateSelectOpen, openDateSearchStep])

  const { checkin, checkout } = useMemo(
    () => ({
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    }),
    [checkinDate, checkoutDate],
  )

  const guestsDescription = useMemo(
    () => getOccupancyDescription(occupancies, intl),
    [occupancies, intl],
  )
  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: goToNextStep,
  })

  const hvSearchLabel = intl.formatMessage({
    id: '05zAIFIwSxBhtDNvmpwl',
    defaultMessage: 'Search',
    description: 'Search button text for homes and villas date panel',
  })
  const hvDateApplyLabel = intl.formatMessage({
    id: '_g6fgtuk6-xFkCzfp8G7',
    defaultMessage: 'Apply',
    description: 'Apply button text for homes and villas date panel in the search interface',
  })
  const hvTravellersSearchLabel = intl.formatMessage({
    id: 'UimF4GxU_m-EI12Dn-7e',
    defaultMessage: 'Search',
    description: 'Search button text for homes and villas travellers panel',
  })
  const hvTravellersApplyLabel = intl.formatMessage({
    id: 'Wo8NjLWMb9kyUUtAUHqw',
    defaultMessage: 'Apply',
    description: 'Apply button text for homes and villas travellers panel',
  })

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        isOpen={activeStep === searchSteps.LOCATION}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        placeholder={locationLabels.homesAndVillasPlaceholderLabel}
        updateSearchSessionId={updateSearchSessionId}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? hvSearchLabel : hvDateApplyLabel}
      />

      <TravellersFloatingPanel
        isOpen={activeStep === searchSteps.GUESTS}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        saleUnit="property"
        confirmText={isSearchEnabled ? hvTravellersSearchLabel : hvTravellersApplyLabel}
      />

      <FieldsWrapper>
        <SearchFormField
          label={intl.formatMessage({
            id: 'dUike6H57eNTyErX99xx',
            defaultMessage: 'Where',
            description: 'Label for homes and villas destination search field',
          })}
          placeholder={intl.formatMessage({
            id: 'X_BTZEiN2QQ_phyy2Qob',
            defaultMessage: 'Search destination, landmark or property',
            description: 'Placeholder for homes and villas destination search field',
          })}
          value={searchItem?.format.mainText}
          onClick={openLocationSearchStep}
        />

        <SearchFormFieldGroup>
          {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <SearchFormField
              label={intl.formatMessage({
                id: 'DqLBIwcVuu2wsccuWBmU',
                defaultMessage: 'When',
                description: 'Label for homes and villas date search field in the search interface',
              })}
              onClick={openDateSearchStep}
              placeholder={intl.formatMessage({
                id: 'IozK-kOpPsfPtn6TcW9U',
                defaultMessage: 'Anytime',
                description: 'Placeholder for homes and villas date search field when no dates selected',
              })}
              value={flexibleSearchSelectionText}
            />
          )}
          {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <>
              <SearchFormField
                label={intl.formatMessage({
                  id: '_wBMA4fNLkote-o7L1iw',
                  defaultMessage: 'Check in',
                  description: 'Label for homes and villas check-in date field on the form',
                })}
                placeholder={intl.formatMessage({
                  id: 'LAFCv7KcU3HlNfNGshML',
                  defaultMessage: 'Anytime',
                  description: 'Placeholder for homes and villas check-in date field',
                })}
                value={checkin}
                onClick={openDateSearchStep}
              />
              <SearchFormField
                label={intl.formatMessage({
                  id: 'z_NW5WhIYS1v8eaRBH45',
                  defaultMessage: 'Check out',
                  description: 'Label for homes and villas check-out date field on the form',
                })}
                placeholder={intl.formatMessage({
                  id: 'AtbTu8NfGrZzl58iJ7Tw',
                  defaultMessage: 'Anytime',
                  description: 'Placeholder for homes and villas check-out date field',
                })}
                value={checkout}
                onClick={openDateSearchStep}
              />
            </>
          )}
        </SearchFormFieldGroup>
        <SearchFormField
          label={intl.formatMessage({
            id: 'eWJ-yyL8EONaGM5oAizl',
            defaultMessage: 'Guests',
            description: 'Label for homes and villas guests/occupancy field on the form',
          })}
          value={guestsDescription}
          onClick={openGuestsSearchStep}
          hasError={areRoomErrorsShown}
        />
      </FieldsWrapper>
    </ContentWrapper>
  )
}

export default HomesAndVillasTabContent
